howspace.components.simpleTextEditor = (() => {
    'use strict';

    const END_OF_PASTED_CONTENT = '___END_OF_PASTED_CONTENT___';
    const dataStorage = {};
    const getData = (editor, key) => {
        return dataStorage[editor.dataset.uid]?.[key] ?? null;
    };
    const setData = (editor, key, data) => {
        dataStorage[editor.dataset.uid][key] = data;
    };

    const dispatchContentChanged = editor => {
        if (editor instanceof jQuery) {
            editor = editor[0];
        }
        const content = howspace.components.simpleTextEditor.getContent(editor, false);
        const isEmpty = !hasContent(editor);
        const label = editor.closest('.howspace-simple-text-editor-label');
        if (isEmpty) {
            label.classList.add('empty');
        } else {
            label.classList.remove('empty');
        }
        howspace.dispatchEvent(editor, howspace.components.simpleTextEditor.CONTENT_CHANGED,
            {
                content: content,
                isEmpty: isEmpty
            }
        );
    };

    const getSelectionRange = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            return selection.getRangeAt(0);
        }
        return null;
    };

    const updateStorage = (editor) => {
        if (editor.id && editor.dataset.useLocalStorage !== 'false') {
            const content = editor.innerHTML.trim();
            if (content === '' || content === '<br>') {
                real.localStorage.remove(editor.id);
            } else {
                real.localStorage.set(editor.id, content);
            }
        }
    };

    const cleanEditor = editor => {
        if (editor instanceof jQuery) {
            editor = editor[0];
        }

        if (!editor) {
            return;
        }

        // Temporarily replace user mentions so that we can utilize innerText to clean up the content.
        editor.querySelectorAll('.user-mention, .user-profile').forEach(mention => {
            const userId = mention.dataset.userId;
            const userDisplayName = mention.textContent;
            mention.outerHTML = `[USER:${userId}:${userDisplayName}]`;
        });

        editor.querySelectorAll('.userlist-mention').forEach(mention => {
            const userId = mention.dataset.userlistId;
            const userDisplayName = mention.textContent;
            mention.outerHTML = `[USERLIST:${userId}:${userDisplayName}]`;
        });

        editor.querySelectorAll('.team-mention').forEach(mention => {
            const userId = mention.dataset.teamId;
            const userDisplayName = mention.textContent;
            mention.outerHTML = `[TEAM:${userId}:${userDisplayName}]`;
        });

        // The contenteditable innerText property doesn't handle line breaks consistently.
        // Let's do some HTML cleaning to fix that.
        let html = editor.innerHTML;
        // Make sure that any text content in the beginning, followed by a <div> element, has a line break.
        html = html.replace(/^([^<]+)<div>/, '$1<br><div>');
        // Replace all line breaks with a <br> tag.
        html = html.replace(/\n/g, '<br>');
        // Replace all <div><br></div> with a single <br> tag.
        html = html.replace(/<div><br><\/div>/g, '<br>');
        // Remove any excess <br> at the end of </div> tags.
        html = html.replace(/<br><\/div>/g, '</div>');
        // Replace all <div> elements with just text followed by a <br> tag.
        html = html.replace(/<div>([\s\S]*?)<\/div>/gi, '$1<br>');
        // Put the cleaned up HTML back to the editor.
        editor.innerHTML = html;

        // Now that the HTML is cleaned the innerText property should give us correct line breaks.
        // Trim that, encode any special characters, and replace all line breaks with <br> tags.
        let cleanHTML = htmlSpecialChars(editor.innerText).trim().replace(/\n/g, '<br>')

        // Restore user mentions.
        cleanHTML = cleanHTML.replace(/\[USER:([^:]+):([^\]]+)]/g, '<span class="user-mention" data-user-id="$1">$2</span>');
        cleanHTML = cleanHTML.replace(/\[USERLIST:([^:]+):([^\]]+)]/g, '<span class="userlist-mention" data-userlist-id="$1">$2</span>');
        cleanHTML = cleanHTML.replace(/\[TEAM:([^:]+):([^\]]+)]/g, '<span class="team-mention" data-team-id="$1">$2</span>');

        // Now sanitize the entire HTML.
        cleanHTML = DOMPurify.sanitize(cleanHTML);

        // Decode nbsps to regular spaces so they won't get converted to \xc2\xa0.
        cleanHTML = cleanHTML.replaceAll('&nbsp;', ' ').replace(/ {2,}/, ' ').trim();

        if (editor.innerHTML !== cleanHTML) {
            editor.innerHTML = cleanHTML;
        }

        if (cleanHTML.match(END_OF_PASTED_CONTENT)) {
            // Remove the pasted content string and move the cursor to the end of the pasted content.
            try {
                editor.childNodes.forEach(child => {
                    if (child.nodeType === Node.TEXT_NODE && child.textContent.includes(END_OF_PASTED_CONTENT)) {
                        child.textContent = child.textContent.replace(END_OF_PASTED_CONTENT, '');
                        const selection = window.getSelection();
                        selection.collapse(child, child.textContent.length);
                        selection.collapseToEnd();
                    }
                });
            } catch (e) {
                // Ignore errors.
            }
        }

        updateStorage(editor);
        dispatchContentChanged(editor);
    };

    const setContent = (editor, content) => {
        if (editor instanceof jQuery) {
            editor = editor[0];
        }
        if (editor) {
            editor.innerHTML = content;
        }
    };

    const hasContent = editor => {
        if (editor instanceof jQuery) {
            editor = editor[0];
        }
        if (!editor) {
            return false;
        }
        return editor.innerText.trim() !== '';
    };

    const handleKeyDown = e => {
        if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Tab') {
            return;
        }

        const editorLabel = e.target.closest('.howspace-simple-text-editor-label');
        const mentionUserPicker = editorLabel.querySelector('.mention-user-picker');

        if (mentionUserPicker === null) {
            // No user picker, nothing to do.
            return;
        }

        // The load more control is navigable but is not a person, so it carries its own class rather than `user`.
        // Enter on it must reach its own handler and not addUser, which would insert nothing and close the picker.
        const activeUser = mentionUserPicker?.querySelector('.user.active, .user-picker-more-btn.active') ?? null;

        if (activeUser === null || mentionUserPicker?.querySelector('.dropdown.expanded') === null) {
            // User picker is not visible or no user is active in the picker.
            return;
        }

        e.stopImmediatePropagation();
        e.preventDefault();

        // Keeps the newly active entry on screen as the highlight moves.
        const activate = entry => {
            if (entry === null) {
                return;
            }
            entry.classList.add('active');
            entry.scrollIntoView({ block: 'nearest' });
        };

        const navigable = entry => entry?.classList.contains('user')
            || entry?.classList.contains('user-picker-more-btn');

        if (e.key === 'Enter' || e.key === 'Tab') {
            activeUser.classList.remove('active');
            // Kept active across the click for the control: its handler moves the highlight on once the page it
            // asked for has arrived, and a row that is being chosen closes the picker anyway.
            if (activeUser.classList.contains('user-picker-more-btn')) {
                activeUser.classList.add('active');
            }
            $(activeUser).trigger('click');
        } else if (e.key === 'ArrowDown') {
            activeUser.classList.remove('active');
            const next = activeUser.nextElementSibling;
            activate(navigable(next) ? next : mentionUserPicker.querySelector('.user, .user-picker-more-btn'));
        } else if (e.key === 'ArrowUp') {
            activeUser.classList.remove('active');
            const previous = activeUser.previousElementSibling;
            if (navigable(previous)) {
                activate(previous);
            } else {
                const entries = mentionUserPicker.querySelectorAll('.user, .user-picker-more-btn');
                activate(entries[entries.length - 1] ?? null);
            }
        }
    };

    const storeLatestCaretPosition = (editor) => {
        const range = getSelectionRange();
        if (range) {
            setData(editor, 'selection-start-node', range.startContainer);
            setData(editor, 'selection-start-offset', range.startOffset);
            setData(editor, 'selection-end-node', range.endContainer);
            setData(editor, 'selection-end-offset', range.endOffset);
        }
    };

    const handleKeyUp = e => {
        storeLatestCaretPosition(e.currentTarget);
    };

    const handlePointerUp = e => {
        storeLatestCaretPosition(e.currentTarget);
    };

    const MENTION_SELECTOR = '.user-mention, .userlist-mention, .team-mention';

    const isMentionNode = node => node?.nodeType === Node.ELEMENT_NODE && node.matches(MENTION_SELECTOR);

    /**
     * The node the caret is about to delete, or null when the browser is deleting a character or merging lines.
     * Skips the empty text nodes Range.insertNode leaves behind when it splits a text node at its end.
     */
    const nodeToDelete = (range, forward) => {
        const container = range.startContainer;
        let node;

        if (container.nodeType === Node.TEXT_NODE) {
            const atEdge = forward ? range.startOffset === container.nodeValue.length : range.startOffset === 0;
            if (!atEdge) {
                // Text remains on that side of the caret, so the browser is deleting a character.
                return null;
            }
            node = forward ? container.nextSibling : container.previousSibling;
        } else {
            node = container.childNodes[forward ? range.startOffset : range.startOffset - 1] ?? null;
        }

        while (node?.nodeType === Node.TEXT_NODE && node.nodeValue === '') {
            node = forward ? node.nextSibling : node.previousSibling;
        }

        return node ?? null;
    };

    /**
     * Deletes the mention next to the caret. Mentions are contenteditable=false, so the browser will not edit them,
     * and nothing else here removes them: they have been going away only as a side effect of the observer in init,
     * which fires when the browser reaches into the span at the caret boundary and drops the whole mention as
     * tampering. Whether it reaches in depends on what sits around the span, and a mention it leaves alone cannot
     * be deleted at all.
     *
     * On beforeinput rather than keydown, because Android keyboards report no key for backspace, only a generic
     * code, and the deletion input types are raised there all the same. Deletes by selecting the mention and
     * handing it back to the browser, which keeps it on the undo stack that removing the node outright bypasses.
     * Except when a line break precedes the mention: Chrome then deletes that too and joins the lines, so those
     * are removed by hand at the cost of undo.
     */
    const BACKWARD_DELETES = ['deleteContentBackward', 'deleteWordBackward'];
    const FORWARD_DELETES = ['deleteContentForward', 'deleteWordForward'];

    // Skips the empty text nodes Range.insertNode leaves behind, as nodeToDelete does.
    const precededByLineBreak = node => {
        let previous = node.previousSibling;
        while (previous?.nodeType === Node.TEXT_NODE && previous.nodeValue === '') {
            previous = previous.previousSibling;
        }
        return previous?.nodeName === 'BR';
    };

    const handleMentionDeletion = e => {
        // A mention next to the caret is the word a word delete removes. Line deletes are left to the browser: they
        // clear to the edge of the line, not one node.
        const inputType = e.originalEvent?.inputType;
        const forward = FORWARD_DELETES.includes(inputType);
        if (!forward && !BACKWARD_DELETES.includes(inputType)) {
            return;
        }

        if (e.originalEvent?.isComposing) {
            // The deletion belongs to the input method while it is composing.
            return;
        }

        const range = getSelectionRange();
        if (range === null || !range.collapsed) {
            // A selection is the browser's to delete, as one. Engines that also raise beforeinput for the editing
            // command below land here with the mention selected, and returning lets that delete through. Chrome
            // raises only input for it.
            return;
        }

        const editor = e.currentTarget;
        if (!editor.contains(range.startContainer)) {
            return;
        }

        const mention = nodeToDelete(range, forward);
        if (!isMentionNode(mention)) {
            return;
        }

        e.preventDefault();

        const selection = window.getSelection();

        if (!precededByLineBreak(mention)) {
            const spanning = document.createRange();
            spanning.selectNode(mention);
            selection.removeAllRanges();
            selection.addRange(spanning);
            document.execCommand('delete');

            if (!mention.isConnected) {
                // That delete raised its own input event, so handleInput has already done the bookkeeping. The
                // stored caret is not part of that: keyup refreshes it, which a soft keyboard may not send, and the
                // browser may have merged the text nodes either side of the mention, so it is stored here too.
                storeLatestCaretPosition(editor);
                return;
            }
        }

        // The browser declined to delete it, or was not asked to. Take the node out and do by hand what the input
        // event would have triggered, handleMentions included: it re-stores the path to the text node the picker
        // inserts at, and the child indexes that path is built from have just shifted.
        const parent = mention.parentNode;
        const caretOffset = Array.prototype.indexOf.call(parent.childNodes, mention);
        mention.remove();

        const caret = document.createRange();
        caret.setStart(parent, caretOffset);
        caret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(caret);

        storeLatestCaretPosition(editor);
        updateStorage(editor);
        dispatchContentChanged(editor);
        handleMentions(editor.closest('.howspace-simple-text-editor-label'), editor);
    };

    const getChildIndexPathFromEditor = (node, childIndexesArr = []) => {
        if (node?.parentElement === null) {
            return childIndexesArr;
        }
        childIndexesArr.unshift(Array.prototype.indexOf.call(node.parentElement.childNodes, node));
        if (node.parentElement.classList.contains('howspace-simple-text-editor')) {
            return childIndexesArr;
        }
        return getChildIndexPathFromEditor(node.parentElement, childIndexesArr);
    };

    const handleInput = e => {
        const editor = e.currentTarget;
        const label = editor.closest('.howspace-simple-text-editor-label');
        dispatchContentChanged(editor);
        updateStorage(editor);
        handleMentions(label, editor);
    };

    const handleMentions = (label, editor) => {
        const mentionUserPicker = label.querySelector('.mention-user-picker');
        if (mentionUserPicker === null) {
            return;
        }

        const selectionRange = getSelectionRange();
        if (!selectionRange) {
            return;
        }

        const text = selectionRange.commonAncestorContainer.nodeValue ?? '';
        const wordBefore = text.substring(0, selectionRange.startOffset).split(/\s/).pop();
        const searchInput = mentionUserPicker.querySelector('.howspace-form-search-input');
        const origValue = searchInput.value;
        const isMention = wordBefore.startsWith('@');
        const searchExpression = isMention ? wordBefore.substring(1).trim() : '';

        const wasAllowEmpty = searchInput.dataset.allowEmpty === 'true';
        searchInput.dataset.allowEmpty = isMention ? 'true' : 'false';

        if (searchExpression !== origValue || (!isMention && wasAllowEmpty) || (isMention && searchExpression === '')) {
            // The DOM nodes can change within the text editor, so we need to store path to the text node.
            // When clicking the mentioned user in search results we get the text node by following this path.
            const pathToTextNode = getChildIndexPathFromEditor(selectionRange.commonAncestorContainer);
            setData(editor, 'mention-path-to-text-node', pathToTextNode);
            setData(editor, 'mention-offset', selectionRange.startOffset);

            searchInput.value = searchExpression;
            searchInput.dispatchEvent(new Event('paste', {bubbles: true}));

            const editorRect = label.getBoundingClientRect();
            const selectionRect = selectionRange.getClientRects()[0] ?? null;
            if (editorRect && selectionRect) {
                mentionUserPicker.style.top = selectionRect.top - editorRect.top + 'px';
                mentionUserPicker.style.left = selectionRect.left - editorRect.left - 16 + 'px';
            }
        }
    };

    const htmlSpecialChars = (text) => {
        return text
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    };

    const handlePaste = e => {
        e.preventDefault();

        const selection = window.getSelection();
        if (selection.rangeCount === 0) {
            return;
        }

        // The clipboardData.getData() method also supports 'text/html' format
        // (just in case we want to support pasting rich text in the future).
        const text = e.originalEvent.clipboardData?.getData('text/plain') || '';

        const textNode = document.createTextNode(text.trim() + END_OF_PASTED_CONTENT);
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(textNode);
        selection.collapseToEnd();

        const editor = e.currentTarget;
        requestAnimationFrame(() => {
            cleanEditor(editor);
        });
    };

    const insertDataAtCursor = (editor, data) => {
        if (editor instanceof jQuery) {
            editor = editor[0];
        }
        if (!editor) {
            return;
        }
        editor.focus();
        const selectionStartNode = getData(editor, 'selection-start-node');
        const selectionEndNode = getData(editor, 'selection-end-node');
        if (selectionStartNode && selectionEndNode) {
            const newRange = document.createRange();
            newRange.setStart(selectionStartNode, getData(editor, 'selection-start-offset'));
            newRange.setEnd(selectionEndNode, getData(editor, 'selection-end-offset'));
            window.getSelection().removeAllRanges();
            window.getSelection().addRange(newRange);
        }

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            let commonAncestor = range.commonAncestorContainer;
            if (commonAncestor.nodeType === Node.TEXT_NODE) {
                commonAncestor = commonAncestor.parentNode;
            }
            if (commonAncestor === editor || commonAncestor.closest('.howspace-simple-text-editor') === editor) {
                const textNode = document.createTextNode(data);
                range.deleteContents();
                range.insertNode(textNode);

                const newRange = document.createRange();
                newRange.setStart(textNode, textNode.length);
                newRange.setEnd(textNode, textNode.length);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(newRange);
            } else {
                // The selection is not within the editor, insert the data at the end.
                editor.innerHTML += data;
            }
        } else {
            // No selection, insert the data at the end.
            editor.innerHTML += data;
        }

        storeLatestCaretPosition(editor);

        const label = editor.closest('.howspace-simple-text-editor-label');
        updateStorage(editor);
        handleMentions(label, editor);
    };

    return {
        CONTENT_CHANGED: 'howspace-simple-text-editor-content-changed',
        init: (elem) => {
            elem.findIncludingSelf('.howspace-simple-text-editor:not(.howspace-simple-text-editor-initialized)').each(function () {
                const editor = $(this);
                const editorLabel = editor.closest('.howspace-simple-text-editor-label');

                const uid = editor[0].id + '-' + real.uid();
                editor.addClass('howspace-simple-text-editor-initialized');
                editor.dataAndAttr('uid', uid);

                dataStorage[uid] = {};

                editor.on('pointerup', handlePointerUp);
                editor.on('input', handleInput);
                editor.on('paste', handlePaste);
                editor.on('keydown', handleKeyDown);
                editor.on('beforeinput', handleMentionDeletion);
                editor.on('keyup', handleKeyUp);

                const value = real.localStorage.get(editor.attr('id'));
                if (value !== null && value !== '') {
                    editor.html(value);
                    if (hasContent(editor)) {
                        dispatchContentChanged(editor);
                    }
                }

                const mentionUserPicker = editorLabel.find('.mention-user-picker');
                if (mentionUserPicker !== null) {
                    const mentionObserver = new MutationObserver((mutationList) => {
                        for (const mutation of mutationList) {
                            if (mutation.target.textContent.trim() === '' && (
                                mutation.target.classList?.contains('user-mention') ||
                                mutation.target.classList?.contains('userlist-mention') ||
                                mutation.target.classList?.contains('team-mention'))) {
                                // Some mutation caused an empty mention span to the editor, remove it.
                                mutation.target.remove();
                            }
                            const parent = mutation.target.parentNode;
                            if (parent?.classList.contains('user-mention') ||
                                parent?.classList.contains('userlist-mention') ||
                                parent?.classList.contains('team-mention')) {
                                // User tried to manipulate the mention span, remove it.
                                parent.remove();
                            }
                        }
                    });

                    mentionObserver.observe(editor[0], {
                        characterData: true,
                        childList: true,
                        subtree: true,
                    });

                    setData(editor[0], 'mention-observer', mentionObserver);

                    editorLabel.on(howspace.components.userPicker.USERS_SEARCHED, e => {
                        // The first entry, so the highlight matches what the list shows. All users, user lists and
                        // teams sit above the people, so on an empty search Enter takes a group rather than a person.
                        editorLabel.find('.mention-user-picker')
                            .find('.user, .user-picker-more-btn').first().addClass('active');
                    });

                    const addMention = (e, className, idKey, id, displayName) => {
                        const pathFromEditor = getData(editor[0], 'mention-path-to-text-node');
                        const mentionOffset = getData(editor[0], 'mention-offset');

                        let mentionTextNode = editor[0];
                        pathFromEditor.forEach(index => {
                            mentionTextNode = mentionTextNode.childNodes[index];
                        });

                        if (!mentionTextNode) {
                            return;
                        }

                        const searchInput = editorLabel.find('.mention-user-picker .howspace-form-search-input');
                        if (searchInput.length > 0) {
                            searchInput[0].dataset.allowEmpty = 'false';
                        }

                        const text = mentionTextNode.nodeValue;
                        const wordBefore = text.substring(0, mentionOffset).split(/\s/).pop();

                        const range = document.createRange();
                        range.selectNode(mentionTextNode);
                        range.setStart(mentionTextNode, mentionOffset - wordBefore.length);
                        range.setEnd(mentionTextNode, mentionOffset);

                        const userDisplayName = displayName;
                        const span = document.createElement('span');
                        span.classList.add(className);
                        span.dataset[idKey] = id;
                        span.contentEditable = 'false';
                        span.innerHTML = userDisplayName;
                        range.deleteContents();
                        range.insertNode(span);

                        // Add a space after the mention.
                        if (span.nextSibling === null) {
                            span.after(document.createTextNode(' '));
                        }
                        if (!span.nextSibling.nodeValue.startsWith(' ')) {
                            // Use Unicode non-breaking space to ensure the cursor can be placed after the mention.
                            span.nextSibling.nodeValue = '\u00A0' + span.nextSibling.nodeValue;
                        }

                        const newRange = document.createRange();
                        newRange.selectNode(span.nextSibling);
                        try {
                            newRange.setStart(span.nextSibling, 1);
                        } catch (e) {
                            newRange.setStart(span.nextSibling, 0);
                        }
                        newRange.collapse(true);
                        window.getSelection().removeAllRanges();
                        window.getSelection().addRange(newRange);

                        // Clean away the mentioned user from the user picker.
                        editorLabel.find('.mention-user-picker .users-list').html('');

                        storeLatestCaretPosition(editor[0]);
                        updateStorage(editor[0]);
                        dispatchContentChanged(editor);
                    };

                    editorLabel.on(howspace.components.userPicker.USER_ADDED, e => {
                        addMention(e, 'user-mention', 'userId', e.detail.user.id, e.detail.user.displayName);
                    });

                    editorLabel.on(howspace.components.userPicker.USERLIST_ADDED, e => {
                        addMention(e, 'userlist-mention', 'userlistId', e.detail.userlist.id, e.detail.userlist.displayName);
                    });

                    editorLabel.on(howspace.components.userPicker.TEAM_ADDED, e => {
                        addMention(e, 'team-mention', 'teamId', e.detail.team.id, e.detail.team.displayName);
                    });
                }
            });
        },
        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-simple-text-editor.howspace-simple-text-editor-initialized').each(function () {
                const editor = $(this);
                const editorLabel = editor.closest('.howspace-simple-text-editor-label');

                editor.removeClass('howspace-simple-text-editor-initialized');

                editor.off('pointerup', handlePointerUp);
                editor.off('input', handleInput);
                editor.off('paste', handlePaste);
                editor.off('keydown', handleKeyDown);
                editor.off('beforeinput', handleMentionDeletion);
                editor.off('keyup', handleKeyUp);

                const mentionUserPicker = editorLabel.find('.mention-user-picker');
                if (mentionUserPicker !== null) {
                    const mentionObserver = getData(editor[0], 'mention-observer');
                    mentionObserver?.disconnect();
                }

                delete dataStorage[editor[0].dataset.uid];
            });
        },
        getContent: (editor, cleanup = true) => {
            if (editor instanceof jQuery) {
                editor = editor[0];
            }
            if (!editor) {
                return '';
            }
            if (cleanup) {
                cleanEditor(editor);
            }
            return editor.innerHTML.trim();
        },
        setContent: (editor, content) => {
            setContent(editor, content);
            dispatchContentChanged(editor);
        },
        hasContent: hasContent,
        clear: (editor) => {
            if (editor instanceof jQuery) {
                editor = editor[0];
            }
            if (!editor) {
                return;
            }
            editor.innerHTML = '';
            if (editor.id) {
                real.localStorage.remove(editor.id);
            }
        },
        insertDataAtCursor: insertDataAtCursor,
        dispatchContentChanged: dispatchContentChanged
    };

})();
